import { Icon, IContextualMenuItem, Spinner, SpinnerSize } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { BackgroundTaskStatusEnum } from "../../../src/taskpane/components/enums/BackgroundTaskStatusEnum";
import { RootState } from "../../../src/taskpane/redux";
import { setContentManagerExchangeProgress } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { setIsBusySaving } from "../../../src/taskpane/redux/modules/savingInBackground";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { apiGetBackgroundTaskProgess } from "../../../src/taskpane/services/ApiService";

export function useMenuItem_SavingFiles(
  setIsCMMoveFilesListPanelOpen: (isOpen: boolean) => void,
  setIsFileUploadProgressPanelOpen: (isOpen: boolean) => void
) {
  const languageStrings = React.useContext(LanguageContext);
  const dispatch = useDispatch();

  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const exchangeProgress = useSelector((state: RootState) => state.contentManagerDirectory.exchange.progress);

  React.useEffect(() => {
    const isPollingActive = exchangeProgress?.taskId &&
      (exchangeProgress.status === BackgroundTaskStatusEnum.Inprogress ||
        exchangeProgress.status === BackgroundTaskStatusEnum.Queued);

    if (!isPollingActive) {
      return undefined;
    }

    const updateProgress = async () => {
      try {
        const progressRes = await apiGetBackgroundTaskProgess(exchangeProgress.taskId);
        dispatch(setContentManagerExchangeProgress(progressRes));

        if (progressRes.status === BackgroundTaskStatusEnum.Completed || progressRes.status === BackgroundTaskStatusEnum.Failure) {
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: progressRes.status === BackgroundTaskStatusEnum.Completed }));
        }
      } catch (error) {
        const message = error.data?.title ? `${languageStrings.Error}: ${error.data?.title}` : languageStrings.ErrorMessageGeneric;
        logger(error, "useMenuItem_SavingFiles.tsx (polling update)");

        dispatch(setContentManagerExchangeProgress({ ...exchangeProgress, status: BackgroundTaskStatusEnum.Failure, message }));
        dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: [message] }));
      }
    };

    const timerId = setTimeout(updateProgress, 3000);

    return () => clearTimeout(timerId);

  }, [exchangeProgress, dispatch, languageStrings]);

  const onGenericSavingClick = React.useCallback(() => setIsFileUploadProgressPanelOpen(true), [setIsFileUploadProgressPanelOpen]);
  const onExchangeProgressClick = React.useCallback(() => setIsCMMoveFilesListPanelOpen(true), [setIsCMMoveFilesListPanelOpen]);

  const menuItem_SavingFiles = React.useMemo(() => {
    if (!isBusySaving) {
      return null;
    }

    // Case 1: A background task (like copy/move) is running.
    if (exchangeProgress?.status) {
      const { status, message } = exchangeProgress;

      const renderIcon = () => {
        if (status === BackgroundTaskStatusEnum.Inprogress || status === BackgroundTaskStatusEnum.Queued) {
          return <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />;
        }
        if (status === BackgroundTaskStatusEnum.Completed) {
          return <Icon iconName="CheckMark" style={{ paddingRight: 5 }} />;
        }
        if (status === BackgroundTaskStatusEnum.Failure) {
          return <Icon iconName="Error" style={{ paddingRight: 5 }} />;
        }
        return null;
      };

      return {
        key: "savingFilesCount",
        text: message || languageStrings.SavingFiles,
        disabled: false,
        onRenderIcon: renderIcon,
        onClick: onExchangeProgressClick,
      };
    }

    // Case 2: A generic "saving" action (like an upload) is running.
    return {
      key: "savingFiles",
      text: languageStrings.SavingFiles,
      disabled: false,
      onRenderIcon: () => <Spinner size={SpinnerSize.small} style={{ paddingRight: 5 }} />,
      onClick: onGenericSavingClick,
    };

  }, [isBusySaving, exchangeProgress, languageStrings, onGenericSavingClick, onExchangeProgressClick]);

  return {
    menuItem_SavingFiles
  };
}